import { useCallback, useEffect, useState, Dispatch, SetStateAction } from 'react';
import { recall, remember } from '../services/viewMemory';

/**
 * `useState`, das den Wert ueber das Demontieren der Ansicht hinweg behaelt.
 *
 * Erster Aufruf: gemerkter Wert, sonst `initial`. Jede Aenderung wird sofort
 * abgelegt (viewMemory). Der Schluessel muss je Ansicht eindeutig sein und
 * sollte lesbar bleiben (`admin.integrity.filter`), damit `forget('admin.')`
 * einen ganzen Teilbaum treffen kann.
 *
 * Nur fuer USER-GEWAEHLTEN Zustand: Reiter, Filter, Suchtext, Aufgeklapptes.
 * Nichts Fluechtiges (Ladezustand, Fehler, Dialoge) — das soll nach einem
 * Neuladen gerade nicht wiederkommen.
 */
export function useRememberedState<T>(key: string, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const saved = recall<T>(key);
    if (saved !== undefined) return saved;
    return typeof initial === 'function' ? (initial as () => T)() : initial;
  });
  useEffect(() => { remember(key, value); }, [key, value]);
  return [value, setValue];
}

/** Wie lange nach dem Anhaengen noch auf Inhalt gewartet wird, bevor die Wiederherstellung aufgibt. */
const RESTORE_TIMEOUT_MS = 5000;

/**
 * Bildlaufposition eines Containers merken und beim naechsten Aufbau
 * wiederherstellen.
 *
 * Rueckgabe ist ein Ref-Callback fuer das Element mit `overflow-y-auto`.
 * Wechselt der Schluessel (z. B. je Reiter), haengt React das Element neu
 * an, und die Position des neuen Schluessels wird hergestellt.
 *
 * Die Tuecke: beim Anhaengen ist die Liste meist noch leer (Daten kommen
 * per Abfrage), `scrollTop` laesst sich dann nicht setzen. Deshalb wartet
 * der Hook mit einem MutationObserver, bis der Inhalt hoch genug ist, und
 * setzt dann. Solange die Wiederherstellung aussteht, wird NICHT gespeichert:
 * ein leerer Container meldet scrollTop 0, und das haette die gemerkte
 * Position ueberschrieben, bevor sie je gesetzt war. Greift der Nutzer
 * selbst ein (Rad, Touch, Tastatur, Zeiger), ist seine Position die neue
 * Wahrheit und das Warten endet.
 */
export function useRememberedScroll(key: string): (el: HTMLElement | null) => void {
  return useCallback((el: HTMLElement | null) => {
    if (!el) return;
    const k = key;
    let pending = recall<number>(k) ?? 0;
    let restored = pending <= 0;
    let frame: number | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;

    const save = () => {
      frame = null;
      remember(k, el.scrollTop > 0 ? Math.round(el.scrollTop) : undefined);
    };
    const onScroll = () => {
      if (!restored) return;
      if (frame === null) frame = requestAnimationFrame(save);
    };
    const finish = () => {
      restored = true;
      observer?.disconnect(); observer = null;
      if (timer) { clearTimeout(timer); timer = null; }
    };
    const tryRestore = () => {
      if (restored) return;
      if (el.scrollHeight - el.clientHeight >= pending) {
        el.scrollTop = pending;
        finish();
      }
    };
    // Der Nutzer greift ein: seine Position gilt.
    const onUserInput = () => { if (!restored) finish(); };
    const inputEvents: Array<keyof HTMLElementEventMap> = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

    el.addEventListener('scroll', onScroll, { passive: true });
    if (!restored) {
      inputEvents.forEach(ev => el.addEventListener(ev, onUserInput, { passive: true }));
      tryRestore();
      if (!restored && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(tryRestore);
        observer.observe(el, { childList: true, subtree: true, attributes: true });
        timer = setTimeout(finish, RESTORE_TIMEOUT_MS);
      } else if (!restored) {
        finish();
      }
    }

    return () => {
      // Letzter Stand beim Demontieren — falls der rAF noch aussteht.
      if (frame !== null) { cancelAnimationFrame(frame); if (restored) save(); }
      finish();
      pending = 0;
      el.removeEventListener('scroll', onScroll);
      inputEvents.forEach(ev => el.removeEventListener(ev, onUserInput));
    };
  }, [key]);
}
